import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { PackagingTypeDto } from 'src/app/shared/dto/packaging-type.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';
import { PackagingTypeResponseDto } from '../../shared/dto/pagination/packaging-type-response.dto';

@Injectable({
    providedIn: 'root',
})
export class PackagingTypes extends BaseService {
    private readonly packagingTypesSubject = new BehaviorSubject<PackagingTypeDto[]>([]);
    public packagingTypes$ = this.packagingTypesSubject.asObservable();

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'packaging-types');
    }

    async setupWebSocket(): Promise<void> {
        try {
            this.packagingTypesSubject.next((await this.findAll()).packagingTypes);
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PACKAGING_TYPES_ROOM));
            this.socketService.getNotifications(Messages.PACKAGING_TYPES_CHANGED).subscribe(async () => {
                const response = await this.findAll();
                this.packagingTypesSubject.next(response.packagingTypes);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de tipos de formato. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PACKAGING_TYPES_ROOM));
    }

    async findAll(): Promise<PackagingTypeResponseDto> {
        try {
            const request = this.httpClient.get<PackagingTypeResponseDto>(`${this.url}/findAll`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun tipo de formato. Error: ${e.message}`);
        }
    }

    async create(packagingType: PackagingTypeDto): Promise<PackagingTypeDto> {
        try {
            const request = this.httpClient.post<PackagingTypeDto>(`${this.url}/create`, packagingType);

            const createdPackagingType: PackagingTypeDto = await lastValueFrom(request);

            return createdPackagingType;

        } catch (e: any) {
            throw new Error(`No se ha podido crear el tipo de formato. Error: ${e.message}`);
        }
    }

    async updatePackagingType(packagingType: PackagingTypeDto): Promise<PackagingTypeDto> {
        try {
            const request = this.httpClient.patch<PackagingTypeDto>(`${this.url}/update/${packagingType.id}`, packagingType);

            const updatedPackagingType: PackagingTypeDto = await lastValueFrom(request);

            return updatedPackagingType;

        } catch (e: any) {
            throw new Error(`No se ha podido actualizar el tipo de formato. Error: ${e.message}`);
        }
    }

    async deletePackagingType(packagingTypeId: number): Promise<PackagingTypeDto> {
        try {
            const request = this.httpClient.delete<PackagingTypeDto>(`${this.url}/delete/${packagingTypeId}`);

            const response: PackagingTypeDto = await lastValueFrom(request);

            return response;
        } catch (e: any) {
            throw new Error(`No se ha podido eliminar el tipo de formato ${packagingTypeId}. Error: ${e.message}`);
        }
    }
}
